import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { Button } from '@/ui/components'
import { VehicleProfile } from '@/shared/api/vehicles'
import { ModificationCategory } from '@/shared/api'

interface VehicleSpecsProps {
	vehicle: VehicleProfile
	modificationsCount?: number
	onEdit?: (vehicle: VehicleProfile) => void
	onEquipmentClick?: (category: ModificationCategory) => void
}

export function VehicleSpecs({ vehicle, modificationsCount = 0, onEdit, onEquipmentClick }: VehicleSpecsProps) {
	const getCategoryForEquipment = (equipmentType: string): ModificationCategory => {
		const mapping: Record<string, ModificationCategory> = {
			has_winch: 'recovery',
			has_skid_plates: 'armor',
			has_rock_sliders: 'armor',
			has_roof_rack: 'roof_rack',
			has_roof_top_tent: 'roof_top_tent',
			has_truck_camper: 'truck_camper'
		}
		return mapping[equipmentType] || 'other'
	}

	const handleEquipmentClick = (equipmentType: string) => {
		if (onEquipmentClick) {
			const category = getCategoryForEquipment(equipmentType)
			onEquipmentClick(category)
		}
	}

	return (
		<Card className="border border-border shadow-sm">
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-earth-900 dark:text-foreground">Vehicle Specifications</CardTitle>
					{onEdit ? (
						<Button variant="outline" size="sm" onClick={() => onEdit(vehicle)}>
							Edit
						</Button>
					) : null}
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Quick equipment */}
				<div className="grid grid-cols-2 gap-4">
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Winch</span>
						<Badge 
							variant={vehicle.has_winch ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_winch ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_winch && handleEquipmentClick('has_winch')}
						>
							{vehicle.has_winch ? 'Yes' : 'No'}
						</Badge>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Skid Plates</span>
						<Badge 
							variant={vehicle.has_skid_plates ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_skid_plates ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_skid_plates && handleEquipmentClick('has_skid_plates')}
						>
							{vehicle.has_skid_plates ? 'Yes' : 'No'}
						</Badge>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Rock Sliders</span>
						<Badge 
							variant={vehicle.has_rock_sliders ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_rock_sliders ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_rock_sliders && handleEquipmentClick('has_rock_sliders')}
						>
							{vehicle.has_rock_sliders ? 'Yes' : 'No'}
						</Badge>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Roof Rack</span>
						<Badge 
							variant={vehicle.has_roof_rack ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_roof_rack ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_roof_rack && handleEquipmentClick('has_roof_rack')}
						>
							{vehicle.has_roof_rack ? 'Yes' : 'No'}
						</Badge>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Roof Top Tent</span>
						<Badge 
							variant={vehicle.has_roof_top_tent ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_roof_top_tent ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_roof_top_tent && handleEquipmentClick('has_roof_top_tent')}
						>
							{vehicle.has_roof_top_tent ? 'Yes' : 'No'}
						</Badge>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Truck Camper</span>
						<Badge 
							variant={vehicle.has_truck_camper ? 'default' : 'secondary'}
							className={onEquipmentClick && vehicle.has_truck_camper ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
							onClick={() => vehicle.has_truck_camper && handleEquipmentClick('has_truck_camper')}
						>
							{vehicle.has_truck_camper ? 'Yes' : 'No'}
						</Badge>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<span className="text-sm text-gray-600 dark:text-earth-600">Type</span>
						<p className="font-medium capitalize text-earth-900 dark:text-earth-800">{vehicle.vehicle_type}</p>
					</div>
					<div>
						<span className="text-sm text-gray-600 dark:text-earth-600">Make</span>
						<p className="font-medium text-earth-900 dark:text-earth-800">{vehicle.make}</p>
					</div>
					<div>
						<span className="text-sm text-gray-600 dark:text-earth-600">Model</span>
						<p className="font-medium text-earth-900 dark:text-earth-800">{vehicle.model}</p>
					</div>
					{vehicle.year && (
						<div>
							<span className="text-sm text-gray-600 dark:text-earth-600">Year</span>
							<p className="font-medium text-earth-900 dark:text-earth-800">{vehicle.year}</p>
						</div>
					)}
				</div>
				
				<div className="pt-4 border-t border-border">
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600 dark:text-earth-600">Daily Driver</span>
						<Badge variant={vehicle.is_daily_driver ? 'default' : 'secondary'}>
							{vehicle.is_daily_driver ? 'Yes' : 'No'}
						</Badge>
					</div>
				</div>

				{modificationsCount > 0 && (
					<div className="pt-4 border-t border-border">
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600 dark:text-earth-600">Modifications</span>
							<Badge>{modificationsCount} modification{modificationsCount !== 1 ? 's' : ''}</Badge>
						</div>
					</div>
				)}

				{/* Social Media Links removed - now on user profile */}
			</CardContent>
		</Card>
	)
}

